import React, { useState } from 'react';
import { RouteComponentProps } from 'react-router-dom';
import { FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import CalendarMonthGrid from './components/CalendarMonthGrid';
import EventListPanel from './components/EventListPanel';
import EventModal from './components/EventModal';
import TeamCalendarPublicViewModel from './TeamCalendarPublic.viewmodel';
import { formatMonthTitle } from './components/dateUtils';
import { TeamCalendarEvent } from 'data/schemas/TeamCalendar';

type Props = RouteComponentProps<{ shareToken: string }>;

function TeamCalendarPublicView(props: Props) {
    const { shareToken } = props.match.params;
    const {
        currentMonth,
        selectedDate,
        calendar,
        events,
        isLoading,
        errorMessage,
        selectedDateEvents,
        setCurrentMonth,
        setSelectedDate
    } = TeamCalendarPublicViewModel(shareToken);
    const [selectedEvent, setSelectedEvent] = useState<TeamCalendarEvent | null>(null);
    const handleReadOnlyChange = () => undefined;

    return (
        <div className="min-h-screen bg-gray-100">
            <div className="mx-auto max-w-[1500px] px-4 py-6 lg:px-6">
                <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
                    <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                        <div>
                            <div className="text-sm font-semibold uppercase tracking-[0.2em] text-green-600">
                                Shared Calendar
                            </div>
                            <h1 className="mt-2 text-3xl font-bold text-gray-900">
                                {calendar?.title ?? 'Calendar Viewer'}
                            </h1>
                            <p className="mt-2 text-sm text-gray-500">
                                Read-only view. No login required.
                            </p>
                        </div>
                        <div className="flex items-center gap-3">
                            <button
                                type="button"
                                onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1))}
                                className="rounded-md border border-gray-300 px-3 py-2 text-sm font-semibold text-gray-700 transition hover:bg-gray-50"
                            >
                                <FaChevronLeft />
                            </button>
                            <div className="min-w-[12rem] text-center text-lg font-semibold text-gray-800">
                                {formatMonthTitle(currentMonth)}
                            </div>
                            <button
                                type="button"
                                onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1))}
                                className="rounded-md border border-gray-300 px-3 py-2 text-sm font-semibold text-gray-700 transition hover:bg-gray-50"
                            >
                                <FaChevronRight />
                            </button>
                        </div>
                    </div>
                </div>

                {isLoading && (
                    <div className="mt-4 rounded-lg border border-green-100 bg-green-50 px-4 py-3 text-sm text-green-700">
                        Loading shared calendar...
                    </div>
                )}

                {errorMessage && (
                    <div className="mt-4 rounded-xl border border-red-100 bg-white p-8 text-center shadow-sm">
                        <h2 className="text-xl font-bold text-gray-900">Calendar link unavailable</h2>
                        <p className="mt-2 text-sm text-gray-500">{errorMessage}</p>
                    </div>
                )}

                {!errorMessage && (
                    <div className="mt-6 grid grid-cols-1 gap-6 xl:grid-cols-[minmax(0,1fr)_360px]">
                        <div>
                            <CalendarMonthGrid
                                currentMonth={currentMonth}
                                events={events}
                                selectedDate={selectedDate}
                                onSelectDate={setSelectedDate}
                                onSelectEvent={setSelectedEvent}
                            />
                            {!isLoading && events.length === 0 && (
                                <div className="mt-4 rounded-xl border border-gray-200 bg-white p-6 text-center shadow-sm">
                                    <h2 className="text-lg font-semibold text-gray-900">
                                        No events in this shared range
                                    </h2>
                                    <p className="mt-2 text-sm text-gray-500">
                                        This link has no events in the selected date range.
                                    </p>
                                </div>
                            )}
                        </div>
                        <EventListPanel
                            title="Selected Day"
                            events={selectedDateEvents}
                            onSelectEvent={setSelectedEvent}
                            emptyMessage="No events on this day."
                        />
                    </div>
                )}
            </div>

            <EventModal
                isOpen={selectedEvent !== null}
                draft={
                    selectedEvent
                        ? {
                              id: selectedEvent.id,
                              title: selectedEvent.title,
                              description: selectedEvent.description,
                              startAt: selectedEvent.startAt,
                              endAt: selectedEvent.endAt,
                              isAllDay: selectedEvent.isAllDay,
                              teamName: selectedEvent.teamName,
                              ownerName: selectedEvent.ownerName,
                              ownerCitizenId: selectedEvent.ownerCitizenId,
                              status: selectedEvent.status,
                              eventType: selectedEvent.eventType,
                              color: selectedEvent.color,
                              visibility: selectedEvent.visibility,
                              allowedViewerCitizenIds: selectedEvent.allowedViewerCitizenIds
                                  ? selectedEvent.allowedViewerCitizenIds
                                        .split(',')
                                        .map(value => value.trim())
                                        .filter(Boolean)
                                  : [],
                              allowedViewerTeams: selectedEvent.allowedViewerTeams
                                  ? selectedEvent.allowedViewerTeams
                                        .split(',')
                                        .map(value => value.trim())
                                        .filter(Boolean)
                                  : [],
                              createdBy: selectedEvent.createdBy,
                              createdOn: selectedEvent.createdOn,
                              updatedBy: selectedEvent.updatedBy,
                              updatedOn: selectedEvent.updatedOn
                          }
                        : {
                              title: '',
                              description: '',
                              startAt: new Date().toISOString(),
                              endAt: new Date().toISOString(),
                              isAllDay: false,
                              teamName: '',
                              ownerName: '',
                              ownerCitizenId: '',
                              status: 'planned',
                              eventType: 'general',
                              color: '#2563eb',
                              visibility: 'only_me',
                              allowedViewerCitizenIds: [],
                              allowedViewerTeams: [],
                              createdBy: ''
                          }
                }
                employees={[]}
                availableTeams={[]}
                isEmployeesLoading={false}
                isSaving={false}
                isReadOnly
                onClose={() => setSelectedEvent(null)}
                onChange={handleReadOnlyChange}
                onSubmit={() => undefined}
            />
        </div>
    );
}

export default TeamCalendarPublicView;
